import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { FC, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetLanguages } from "~/api/languages/languages";
import { Language, OrganizationRole } from "~/api/model";
import {
  getGetOrganizationQueryKey,
  getGetOrganizationSettingsQueryKey,
  useGetOrganization,
  useGetOrganizationSettings,
  useUpdateOrganization,
  useUpdateOrganizationSettings,
} from "~/api/organizations/organizations";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { SaveButton, SaveButtonRef } from "~/components/SaveButton";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  PipelineMode,
  PipelineModeSelector,
} from "~/features/cases/components/PipelineModeSelector";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { trackEvent } from "~/utils/analytics";

export const SettingsOrganization: FC = () => {
  useTrackPageVisit("app/settings_organization/visit");

  const intl = useIntl();
  const { toast } = useToast();
  const { organizationId } = useOrganizationParams();
  const { data: organization } = useGetOrganization(organizationId);
  const { data: organizationSettings } =
    useGetOrganizationSettings(organizationId);
  const { data: languages } = useGetLanguages();
  const { mutateAsync: updateOrganizationAsync, isPending } =
    useUpdateOrganization();
  const { mutateAsync: updateSettingsAsync, isPending: isUpdatingSettings } =
    useUpdateOrganizationSettings();
  const saveButtonRef = useRef<SaveButtonRef>(null);
  const queryClient = useQueryClient();

  const [selectedPipelineMode, setSelectedPipelineMode] =
    useState<PipelineMode>("USE_TOC");

  const [currentLanguage, setCurrentLanguage] = useState<
    Language | undefined
  >();

  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  const resolvedLanguage = useMemo(() => {
    const orgLanguage = organization?.language;
    if (!orgLanguage || !languages) return undefined;
    return Object.keys(languages).find(
      (key) => key.toLowerCase() === orgLanguage.toLowerCase()
    ) as Language | undefined;
  }, [organization?.language, languages]);

  useEffect(() => {
    if (resolvedLanguage) {
      setCurrentLanguage(resolvedLanguage);
    }
  }, [resolvedLanguage]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentLanguage) return;

    trackEvent({
      eventName: "app/settings_organization/language.select/change",
      properties: { language: currentLanguage },
    });

    trackEvent({
      eventName: "app/settings_organization/pipeline_mode.save",
      properties: { mode: selectedPipelineMode },
    });

    const [organizationResult, settingsResult] = await Promise.allSettled([
      updateOrganizationAsync({
        organizationId,
        data: { language: currentLanguage },
      }),
      updateSettingsAsync({
        organizationId,
        data: {
          pipelineMode: selectedPipelineMode,
        } as Parameters<typeof updateSettingsAsync>[0]["data"],
      }),
    ]);

    // Keep the cache in sync for whichever mutation succeeded, so a partial
    // failure doesn't leave the UI showing stale settings.
    if (organizationResult.status === "fulfilled") {
      queryClient.invalidateQueries({
        queryKey: getGetOrganizationQueryKey(organizationId),
      });
    }
    if (settingsResult.status === "fulfilled") {
      queryClient.setQueryData(
        getGetOrganizationSettingsQueryKey(organizationId),
        settingsResult.value
      );
    }

    const failure = [organizationResult, settingsResult].find(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );
    if (failure) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "settings.saveFailed",
          defaultMessage: "Couldn't save your changes",
        }),
        description:
          failure.reason instanceof Error ? failure.reason.message : undefined,
      });
      return;
    }

    saveButtonRef.current?.onSuccess();
  };

  const handlePipelineModeChange = (mode: PipelineMode) => {
    setSelectedPipelineMode(mode);
    trackEvent({
      eventName: "app/settings_organization/pipeline_mode.select",
      properties: { mode },
    });
  };

  useEffect(() => {
    if (organizationSettings?.pipelineMode) {
      setSelectedPipelineMode(organizationSettings.pipelineMode);
    }
  }, [organizationSettings]);

  const handleLanguageChange = (value: string) => {
    setCurrentLanguage(value as Language);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-6 mt-2 w-full max-w-[766px]"
    >
      <h3 className="text-2xl font-semibold">
        <FormattedMessage
          id="settings.organization"
          defaultMessage="Organization"
        />
      </h3>

      {/* Output Language Section */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="organizationLanguage">
          <FormattedMessage
            id="settings.organization.outputLanguage"
            defaultMessage="Output Language"
          />
        </Label>
        <Popover
          open={isLanguageDropdownOpen}
          onOpenChange={(isOpen) => {
            setIsLanguageDropdownOpen(isOpen);
            if (isOpen) {
              trackEvent({
                eventName: "app/settings_organization/language.select/click",
              });
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              id="organizationLanguage"
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={isLanguageDropdownOpen}
              className={`w-full justify-between font-normal ${
                currentLanguage ? "" : "text-muted-foreground"
              }`}
            >
              {currentLanguage && languages?.[currentLanguage]
                ? languages[currentLanguage].label
                : intl.formatMessage({
                    id: "settings.organization.selectLanguage",
                    defaultMessage: "Select a language",
                  })}
              <ChevronDown className="opacity-50 w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-(--radix-popover-trigger-width) p-0"
          >
            <Command
              filter={(_, search, keywords = []) => {
                const extendValue = keywords.join(" ");
                if (extendValue.toLowerCase().includes(search.toLowerCase())) {
                  return 1;
                }
                return 0;
              }}
            >
              <CommandInput
                placeholder={intl.formatMessage({
                  id: "settings.organization.searchLanguage",
                  defaultMessage: "Search languages",
                })}
              />
              <CommandList>
                <CommandEmpty>
                  <FormattedMessage
                    id="settings.organization.noLanguageFound"
                    defaultMessage="No language found"
                  />
                </CommandEmpty>
                <CommandGroup>
                  {languages &&
                    Object.entries(languages).map(([key, value]) => (
                      <CommandItem
                        key={key}
                        value={key}
                        keywords={[value.label, value.englishLabel]}
                        onSelect={() => {
                          handleLanguageChange(key);
                          setIsLanguageDropdownOpen(false);
                        }}
                      >
                        {value.label}
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <span className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.organization.outputLanguageHint"
            defaultMessage="This language will be used for AI-generated content such as summaries, classifications, and chat responses."
          />
        </span>
      </div>

      {/* Pipeline Mode Section - Only visible to OWNER and SUPERADMIN */}
      <DisplayFor roles={[OrganizationRole.OWNER, OrganizationRole.SUPERADMIN]}>
        <div className="flex flex-col gap-4 border-t pt-6">
          <div>
            <Label>
              <FormattedMessage
                id="settings.organization.pipelineMode.title"
                defaultMessage="Document Processing Mode"
              />
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              <FormattedMessage
                id="settings.organization.pipelineMode.description"
                defaultMessage="Choose how Sky AI handles document structure and organization for new uploads."
              />
            </p>
          </div>

          <PipelineModeSelector
            selectedMode={selectedPipelineMode}
            onModeChange={handlePipelineModeChange}
            variant="detailed"
          />
        </div>
      </DisplayFor>

      <SaveButton
        isPending={isPending || isUpdatingSettings}
        ref={saveButtonRef}
      />
    </form>
  );
};
